import type { ReactNode } from 'react'
import { Icon, type IconName } from '../Icon'
import { cn } from '../../utils/cn'

type PillTone = 'neutral' | 'success' | 'danger' | 'accent' | 'warning'

const pillToneClasses: Record<PillTone, string> = {
  neutral: 'bg-neutral-25 text-muted',
  success: 'bg-green-100 text-green-800',
  danger: 'bg-danger-soft text-danger',
  accent: 'bg-accent-soft text-primary-800',
  warning: 'bg-amber-100 text-amber-800',
}

type PillProps = {
  tone?: PillTone
  children: ReactNode
  className?: string
}

export function Pill({ tone = 'neutral', children, className }: PillProps) {
  return (
    <span
      className={cn(
        'shrink-0 rounded-full px-2.5 py-0.5 text-[11px] font-semibold',
        pillToneClasses[tone],
        className,
      )}
    >
      {children}
    </span>
  )
}

type ManagementCardProps = {
  /** 指定するとカード全体がタップ可能になり末尾にシェブロンを表示する（主操作） */
  onClick?: () => void
  ariaLabel?: string
  /** 先頭のアバター等 */
  leading?: ReactNode
  title: ReactNode
  /** タイトル右の小さな補助（#ID やセクション数など） */
  titleSuffix?: ReactNode
  /** タイトル行右側のバッジ群 */
  badges?: ReactNode
  /** 本文（補助情報の行） */
  children?: ReactNode
  /** フッターの副操作ボタン群。カードのタップとは独立して動く */
  actions?: ReactNode
  className?: string
}

export function ManagementCard({
  onClick,
  ariaLabel,
  leading,
  title,
  titleSuffix,
  badges,
  children,
  actions,
  className,
}: ManagementCardProps) {
  const inner = (
    <>
      {leading}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="min-w-0 flex-1 truncate text-sm font-semibold text-ink">
            {title}
          </span>
          {titleSuffix != null && (
            <span className="shrink-0 text-xs text-muted">{titleSuffix}</span>
          )}
          {badges}
        </div>
        {children != null && <div className="mt-2 grid gap-1">{children}</div>}
      </div>
      {onClick && (
        <Icon
          icon="chevronLeft"
          className="h-4 w-4 shrink-0 rotate-180 text-muted"
        />
      )}
    </>
  )

  return (
    <div className="relative overflow-hidden rounded-2xl border border-border/70 bg-white">
      {/* 本文は通常のフロー要素のまま描画し、スクリーンリーダーが
          タイトル/メタを読み上げられるようにする（button で包むと content model 違反かつ
          aria-label がメタを上書きして読み上げられなくなる）。 */}
      <div className={cn('flex items-center gap-3 p-4', className)}>{inner}</div>
      {onClick && (
        // カード全体をタップ可能にする透明オーバーレイ（stretched link）。
        // 本文の上に重ねるが、フッターの actions は z で前面に出してクリックを通す。
        <button
          type="button"
          onClick={onClick}
          aria-label={ariaLabel}
          className="absolute inset-0 z-10 rounded-2xl transition active:bg-neutral-25/40"
        />
      )}
      {actions != null && (
        <div className="relative z-20 flex items-center gap-2 border-t border-border/60 px-3 py-2">
          {actions}
        </div>
      )}
    </div>
  )
}

type CardActionTone = 'default' | 'danger'

type ManagementCardActionProps = {
  onClick: () => void
  disabled?: boolean
  tone?: CardActionTone
  icon?: IconName
  children: ReactNode
}

/** カードのフッターに置く副操作ボタン。カード本体のタップに伝播させない */
export function ManagementCardAction({
  onClick,
  disabled = false,
  tone = 'default',
  icon,
  children,
}: ManagementCardActionProps) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={(e) => {
        e.stopPropagation()
        onClick()
      }}
      className={cn(
        'inline-flex h-9 items-center gap-1.5 rounded-[var(--btn-radius)] px-3 text-xs font-semibold transition disabled:opacity-50',
        tone === 'danger'
          ? 'bg-danger-soft text-danger hover:bg-danger-soft/70'
          : 'bg-neutral-25 text-ink hover:bg-neutral-50',
      )}
    >
      {icon && <Icon icon={icon} className="h-3.5 w-3.5" />}
      {children}
    </button>
  )
}

type CardMetaProps = {
  label: ReactNode
  value: ReactNode
  testId?: string
}

/** ラベル＋値の小さな縦並び（最終アクセス: -、配信数: 1,000 など） */
export function CardMeta({ label, value, testId }: CardMetaProps) {
  return (
    <div className="min-w-0" data-testid={testId}>
      <dt className="text-[10px] text-muted">{label}</dt>
      <dd className="truncate text-xs text-ink">{value}</dd>
    </div>
  )
}

type CardMetaGridProps = {
  cols?: 2 | 3
  children: ReactNode
}

export function CardMetaGrid({ cols = 2, children }: CardMetaGridProps) {
  return (
    <dl className={cn('grid gap-2', cols === 3 ? 'grid-cols-3' : 'grid-cols-2')}>
      {children}
    </dl>
  )
}

type ManagementCardListProps = {
  loading?: boolean
  isEmpty?: boolean
  loadingMessage: string
  emptyMessage: string
  className?: string
  children: ReactNode
}

/** カード一覧の外枠。loading/empty を共通の見た目で処理する */
export function ManagementCardList({
  loading = false,
  isEmpty = false,
  loadingMessage,
  emptyMessage,
  className,
  children,
}: ManagementCardListProps) {
  if (loading || isEmpty) {
    return (
      <p className="rounded-2xl border border-border/70 bg-white px-4 py-10 text-center text-sm text-muted">
        {loading ? loadingMessage : emptyMessage}
      </p>
    )
  }

  return <ul className={cn('grid grid-cols-1 gap-3', className)}>{children}</ul>
}
